// „Целият списък" на листа „Изписване" (изнесен от WriteoffWorksheet):
// продуктите по групи + рецептите като собствена група — с ОТРИЦАТЕЛНО id, за
// да не се бият с id-тата на продуктите. Отметнатият ред носи поле
// „Количество" (порции за рецепта), а вече вписаният продукт/рецепта може да
// се отметне — не се дублира, а получава въведеното (`addCatalogLines`).
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Lookup, Product, StockBalance } from "../../../../api/inventory";
import { clampDecimals } from "../../components/qty";
import { pickableProducts } from "../../components/product/deletedProducts";
import {
  CatalogPickerModal,
  type CatalogEntryValues,
  type CatalogItem,
} from "../../components/search/CatalogPickerModal";
import { catalogStockTone } from "../../components/search/catalogStock";
import {
  formatPortionsInput,
  formatQtyInput,
  parseDecimal,
  type AddKind,
  type WriteoffLineDraft,
  type WriteoffSearchResult,
} from "./writeoffWorksheetShared";
import { catalogTargetLineOf, type WriteoffCatalogPick } from "./writeoffCatalogLines";

type Params = {
  products: Product[];
  recipes: Lookup[];
  lines: WriteoffLineDraft[];
  balanceByProduct: Map<number, StockBalance>;
  productResult: (productId: number) => WriteoffSearchResult | null;
  addCatalogLines: (picks: WriteoffCatalogPick[]) => void;
  // затворен без добавяне → фокусът обратно в търсачката
  onDismiss: () => void;
};

export const useWriteoffCatalog = ({
  products,
  recipes,
  lines,
  balanceByProduct,
  productResult,
  addCatalogLines,
  onDismiss,
}: Params) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const items = useMemo<CatalogItem[]>(() => {
    const productsInDocument = new Set(lines.map((line) => line.productId));
    const recipesInDocument = new Set(lines.map((line) => line.recipeId));
    // стойността на вписания ред (ако има) — презаписва се след въпрос
    const filledOf = (kind: AddKind, id: number) => catalogTargetLineOf(lines, kind, id)?.quantity.trim() || undefined;
    const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name, "bg");
    return [
      ...[...pickableProducts(products)].sort(byName).map((product) => {
        const quantity = Math.max(0, balanceByProduct.get(product.id)?.quantity ?? 0);
        return {
          id: product.id,
          name: product.name,
          group: product.category ?? null,
          unit: product.unit ?? undefined,
          stock: formatQtyInput(quantity, clampDecimals(product.decimalPlaces)),
          stockTone: catalogStockTone(quantity, product.minQuantity),
          added: productsInDocument.has(product.id),
          entryUnit: product.unit ?? undefined,
          filledValue: filledOf("product", product.id),
        };
      }),
      ...[...recipes].sort(byName).map((recipe) => ({
        id: -recipe.id,
        name: recipe.name,
        group: t("inventoryPage.woCatalogRecipes"),
        sub: t("inventoryPage.recipe"),
        added: recipesInDocument.has(recipe.id),
        entryUnit: t("inventoryPage.unitPortions"),
        filledValue: filledOf("recipe", recipe.id),
      })),
    ];
  }, [products, recipes, lines, balanceByProduct, t]);

  // Въведеното → текстът на клетката, както при напускане: количеството по
  // мерната единица, порциите без излишни нули
  const quantityOf = (result: WriteoffSearchResult, values: CatalogEntryValues | undefined, catalogId: number) => {
    const typed = values?.get(catalogId)?.qty ?? "";
    if (typed === "") return null;
    return result.kind === "recipe"
      ? formatPortionsInput(parseDecimal(typed))
      : formatQtyInput(parseDecimal(typed), result.decimals);
  };

  const addFromCatalog = (ids: number[], values?: CatalogEntryValues) => {
    setOpen(false);
    const picks = ids
      .map((id): WriteoffCatalogPick | null => {
        const recipe = id < 0 ? recipes.find((r) => r.id === -id) : undefined;
        const result: WriteoffSearchResult | null =
          id >= 0 ? productResult(id) : recipe ? { kind: "recipe", id: recipe.id, name: recipe.name } : null;
        return result ? { result, quantity: quantityOf(result, values, id) } : null;
      })
      .filter((pick): pick is WriteoffCatalogPick => pick != null);
    addCatalogLines(picks);
  };

  const modal = (
    <CatalogPickerModal
      open={open}
      title={t("inventoryPage.woCatalogTitle")}
      hint={t("inventoryPage.woCatalogHint")}
      items={items}
      entry={{ kind: "qty", qtyLabel: t("inventoryPage.colQty") }}
      addedSelectable
      onClose={() => setOpen(false)}
      onAdd={addFromCatalog}
      onAfterClose={(added) => {
        if (!added) onDismiss();
      }}
    />
  );

  return { openCatalog: () => setOpen(true), catalogModal: modal };
};
